import { useEffect, useMemo, useState } from "react";
import useUnit from "./useUnit";

/** Rótulo da unidade nas listas e seletores: o tipo vem na frente do nome ("Matriz - Lumi", "Filial - Lumi SP"). */
export const getSubsidiaryOptionLabel = (option) => (option?.ctm_name ? `${option.ctm_company_type === "PARENT" ? "Matriz" : "Filial"} - ${option.ctm_name}` : "");

/**
 * Centraliza o filtro de filiais.
 *
 * Matriz filtra por si mesma e/ou suas filiais, filial fica travada nela mesma. Visualizando outra unidade pelo
 * seletor da navegação o filtro fica travado nela: a visualização inteira do painel já é daquela unidade.
 *
 * @param {Object} params
 * @param {boolean} [params.autoLoad=true] Busca as filiais na montagem. Use `false` para
 * disparar a busca manualmente (ex.: junto de outras requisições em um `Promise.all`).
 */
export default function useSubsidiaryFilter({ autoLoad = true } = {}) {
    const { units, isParent, selectedUnit, readOnly, loading, loadUnits } = useUnit();

    const [selectedSubsidiary, setSelectedSubsidiary] = useState([]);

    useEffect(() => {
        if (autoLoad) {
            loadUnits();
        }
    }, [autoLoad, loadUnits]);

    // Filtro travado: pela unidade que está sendo visualizada ou, para quem é filial, pela própria empresa.
    const locked = useMemo(() => {
        if (readOnly) return [selectedUnit];

        return isParent ? null : units;
    }, [readOnly, selectedUnit, isParent, units]);

    const subsidiaries = locked ?? units;
    const selected = locked ?? selectedSubsidiary;

    const selectedSubsidiaryIds = useMemo(() => selected.map((obj) => obj.id), [selected]);

    /** Props comuns do CustomAutocomplete de filial. `label` e `disabled` podem ser sobrescritos na página. */
    const subsidiaryAutocompleteProps = useMemo(
        () => ({
            multiple: true,
            disableCloseOnSelect: true,
            name: "filial",
            noOptionsText: "Sem resultados.",
            label: "Filial",
            placeholder: selected.length ? "" : "Selecione",
            handleChange: (_, value) => setSelectedSubsidiary(value),
            options: subsidiaries,
            value: selected,
            disabled: Boolean(locked),
            getOptionLabel: getSubsidiaryOptionLabel,
        }),
        [subsidiaries, selected, locked],
    );

    return {
        subsidiaries,
        isParent,
        selectedSubsidiary: selected,
        setSelectedSubsidiary,
        selectedSubsidiaryIds,
        loading,
        loadSubsidiaries: loadUnits,
        subsidiaryAutocompleteProps,
    };
}
